export const checkEmployerAssignments = /* GraphQL */ `
  query CheckEmployerAssignments($companyID: ID = "", $employerID: ID = "") {
    listEmployerAssignments(
      filter: { companyID: { eq: $companyID }, employerID: { eq: $employerID } }
    ) {
      items {
        id
      }
    }
  }
`;

export const getAttorneyAssignment = /* GraphQL */ `
  query GetCompanyAttorneyAssignment($companyID: ID!, $attorneyID: ID!) {
    getCompany(id: $companyID) {
      assignedAttorneys(attorneyID: { eq: $attorneyID }) {
        items {
          id
        }
      }
    }
  }
`;

export const getEmployerAdminAssignment = /* GraphQL */ `
  query GetCompanyEmployerAdminAssignment($companyID: ID!, $employerID: ID!) {
    getCompany(id: $companyID) {
      assignedEmployerAdmins(employerID: { eq: $employerID }) {
        items {
          id
        }
      }
    }
  }
`;

export const getParalegalAssignment = /* GraphQL */ `
  query GetCompanyParalegalAssignment($companyID: ID!, $paralegalID: ID!) {
    getCompany(id: $companyID) {
      assignedParalegals(paralegalID: { eq: $paralegalID }) {
        items {
          id
        }
      }
    }
  }
`;

export const listCompanyJobDescriptions = /* GraphQL */ `
  query ListCompanyJobDescriptions($id: ID = "") {
    getCompany(id: $id) {
      jobDescriptions(
        limit: 300
        archivedType: { eq: { archived: ACTIVE, type: STANDARD } }
      ) {
        items {
          archived
          canWorkFromHome
          certificationLicenses
          companyID
          createdAt
          degrees
          dotNumber
          caseSocCode
          duties
          id
          majors
          objectiveSkillSetsRequired
          offeredSalary
          officialTitle
          specialRequirements
          supervisesOthers
          supervisesOthersCount
          supervisesTitles
          supervisorTitle
          title
          travelDomestic
          travelInternational
          travelIsRequired
          travelPercentage
          type
          yearsGeneralExperience
        }
      }
    }
  }
`;

// Subsidiary candidates are no longer loaded here. Reading the whole company
// list up front could never be complete anyway — DynamoDB caps a query at 1MB
// regardless of 'limit', so it silently returned ~304 of 495 active companies
// (everything after "P" was unreachable in the Subsidiaries picker). The field
// now searches on keystroke via searchCompanyNames instead, which also drops a
// large wasted read from every open of this form.
export const populateCreateCompanyForm = /* GraphQL */ `
  query PopulateCreateCompanyForm {
    attorneys: listTeamMembersAlpha(
      archivedLowerFull: { beginsWith: { archived: ACTIVE } }
      filter: { position: { eq: ATTORNEY } }
      limit: 200
      type: TeamMember
    ) {
      items {
        id
        firstname
        lastname
        slug
        type
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
      }
    }
    paralegals: listTeamMembersAlpha(
      archivedLowerFull: { beginsWith: { archived: ACTIVE } }
      filter: { position: { eq: PARALEGAL } }
      limit: 200
      type: TeamMember
    ) {
      items {
        id
        firstname
        lastname
        slug
        type
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
      }
    }
  }
`;

export const checkForCompanySlug = /* GraphQL */ `
  query CheckForCompanySlug($slug: String!) {
    companiesBySlug(slug: $slug, limit: 1) {
      items {
        id
      }
    }
  }
`;

export const peopleByEmail = /* GraphQL */ `
  query PeopleByEmail($email: String!) {
    employersByEmail(email: $email, limit: 1) {
      items {
        id
        type
        username
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
        firstname
        lastname
      }
    }
    nationalsByEmail(email: $email, limit: 1) {
      items {
        id
        type
        username
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
        firstname
        lastname
      }
    }
    teamByEmail(email: $email, limit: 1) {
      items {
        id
        type
        username
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
        firstname
        lastname
      }
    }
  }
`;

export const peopleBySlug = /* GraphQL */ `
  query PeopleBySlug($slug: String!) {
    employersBySlug(slug: $slug, limit: 1) {
      items {
        __typename
        id
        firstname
        lastname
      }
    }
    nationalsBySlug(slug: $slug, limit: 1) {
      items {
        __typename
        id
        firstname
        lastname
      }
    }
    teamBySlug(slug: $slug, limit: 1) {
      items {
        __typename
        id
        firstname
        lastname
      }
    }
  }
`;

export const getCurrentTeamMember = /* GraphQL */ `
  query GetCurrentTeamMember($id: ID!) {
    getTeamMember(id: $id) {
      __typename
      type
      id
      slug
      userAgreementAcknowledged
      hasLoggedIn
      email
      position
      avatar {
        original {
          bucket
          key
          level
          region
          identityId
        }
        square {
          bucket
          key
          level
          region
          identityId
        }
        squareThumbnails {
          image {
            bucket
            key
            level
            region
            identityId
          }
          size
        }
        version
      }
      firstname
      lastname
      username
    }
  }
`;

export const getCurrentEmployer = /* GraphQL */ `
  query GetCurrentEmployer($id: ID!) {
    getEmployer(id: $id) {
      __typename
      type
      id
      slug
      userAgreementAcknowledged
      newsletterModalViewCount
      hasLoggedIn
      nationalsWithOpenCases
      email
      isPrimaryContact
      companyIDs
      primaryContactCompanies{
        items {
          id,
          companyID,
          employerID,
        }
      }
      adminCompanies{
        items {
          id
          employerID
          companyID
          company {
            name
            displayCompanyName
            hasCompletedOnboardingQuestions
            onboardingSections {
              companyInfo
              contactInfo
              companyPreferences
              immigrationPreferences
              paymentPreferences
              questions
              employeeInformation
            }
            primaryContacts {
              items {
                id
                companyID
                employerID
              }
            }
          }
        }
      }
      companies {
        items {
          company {
            id
            slug
            name
            displayCompanyName
            onboardingSections {
              companyInfo
              contactInfo
              companyPreferences
              immigrationPreferences
              paymentPreferences
              questions
            }
            primaryContacts {
              items {
                id
                companyID
                employerID
              }
            }
            accountManagerID
            data {
              addressPrimary {
                careOf
                address
                unitNumber
                unitType
                adminArea
                state
                city
                country
                postalCode
                title
              }
              addressSecondary {
                careOf
                address
                unitNumber
                unitType
                adminArea
                state
                city
                country
                postalCode
                title
              }
            }
          }
        }
      }
      avatar {
        original {
          bucket
          key
          level
          region
          identityId
        }
        square {
          bucket
          key
          level
          region
          identityId
        }
        squareThumbnails {
          image {
            bucket
            key
            level
            region
            identityId
          }
          size
        }
        version
      }
      firstname
      middlename
      lastname
      username
      phone
      title
      hasCompletedOnboardingQuestions
    }
  }
`;

export const getEmployerCompanyNames = /* GraphQL */ `
  query GetEmployerCompanyNames($id: ID = "") {
    getEmployer(id: $id) {
      companies(limit: 100) {
        items {
          company {
            id
            name
            displayCompanyName
            slug
          }
        }
      }
    }
  }
`;

export const getCompanyHook = /* GraphQL */ `
  query GetCompanyHook($id: ID!) {
    getCompany(id: $id) {
      id
      name
      slug
      logo {
        version
        original {
          bucket
          key
          level
          region
        }
        thumbnails {
          size
          image {
            bucket
            key
            level
            region
          }
        }
      }
      data {
        utilizePremiumProcessingService
        addressPrimary {
          address
          adminArea
          city
          country
          postalCode
          title
        }
        addressSecondary {
          address
          adminArea
          city
          country
          postalCode
          title
        }
      }
    }
  }
`;

export const getCustomDataCompany = /* GraphQL */ `
  query getCustomDataCompany($id: ID!) {
    getCompany(id: $id) {
      id
      name
      lowerName
      lowerNameKey
      slug
      type
      logo {
        version
        original {
          bucket
          key
          level
          region
        }
        thumbnails {
          size
          image {
            bucket
            key
            level
            region
          }
        }
      }
      domainWhitelist
      hasCompletedOnboardingQuestions
      onboardingSections {
        companyInfo
        contactInfo
        companyPreferences
        immigrationPreferences
        paymentPreferences
        questions
        employeeInformation
      }
      isPartnerCompany
      isParent
      children {
        nextToken
      }
      parent {
        id
        name
        lowerName
        lowerNameKey
        slug
        type
        domainWhitelist
        isPartnerCompany
        isParent
        parentID
        archived
        companyNumber
        nationalCount
        accountManagerID
        openCaseCount
        closedCaseCount
        completedCaseCount
        legalTeamIDs
        createdAt
        updatedAt
        companyDataId
      }
      parentID
      archived
      companyNumber
      nationalCount
      foreignNationals {
        nextToken
      }
      assignedAttorneys {
        nextToken
      }
      assignedParalegals {
        nextToken
      }
      accountManagerID
      accountManager {
        id
        createdBy
        slug
        userAgreementAcknowledged
        userAgreementAcknowledgementDate
        userAgreementAcknowledgementText
        hasLoggedIn
        type
        email
        username
        firstname
        middlename
        lastname
        lowerFirst
        lowerLast
        lowerFull
        phone
        position
        title
        licensingAuthority
        stateBarNumber
        isAdmin
        managerID
        mostRecentlyInvited
        archived
        recentlyViewedCases
        nationalsWithOpenCases
        companiesAssignedTo
        createdAt
        updatedAt
      }
      assignedEmployers {
        nextToken
      }
      assignedEmployerAdmins {
        nextToken
      }
      invoicingContacts {
        nextToken
      }
      primaryContacts {
        nextToken
      }
      seniorContacts {
        nextToken
      }
      signatoryContacts {
        nextToken
      }
      data {
        id
        companyID
        allowsEmployeesToTelecommute {
          value
          followPrompt
          followValue
          followPromptSecond
          followValueSecond
        }
        placesEmployeesAtClientSitesForMoreThan30DaysAtATime
        preferredMailingAddress
        companyCoversExpenseOfDependentPetitionsAndOrEADCards
        companyCoversExpenseOfDependentPetitions
        annualMeetingDate
        requiresMeetingReminder
        doingBusinessAs
        educationEvaluation
        employeesEmailMHAttorneysAndParalegalsDirectly
        fein
        fullTimeEmployeesInUS
        isH1BDependentEmloyer
        hasBeenWillfulViolator
        isHigherEd_20_USC_1001_a
        isHigherEdNonProfit_8_CFR_214_2_h_19_iii_B
        isHigherEdNonProfit_8_CFR_214_2_h_8_ii_F_2
        isNonProfitResearch_8_CFR_214_2_h_19_iii_C
        isNonProfitResearch_8_CFR_214_2_h_8_ii_F_3
        isQualifyingCapExempt_8_CFR_214_2_h_8_ii_F_4
        maintainsAWrittenImmigrationPolicy
        naicsCodeOfCompany
        partTimeEmployeesInUS
        paymentPreference
        permissionPowerAttorney
        permissionReleaseApprovalNotices
        permissionReleasePetitionsRFE
        permissionReleaseLCA
        permissionRelease9089
        phone
        placesEmployeesAtClientSitesForMoreThanACoupleOfDaysAtATime
        fax
        preferredShippingSpeedForDocuments
        shipOriginalApprovalNotices
        staffsEmployeesAtThirdPartyEndClientSites
        typeOfBusiness
        usesWageSurvey
        usesEVerify
        utilizePremiumProcessingService
        technologyReleaseLicenseRequirement
        yearCompanyWasFoundedInUS
        I9_audit_has_been_completed_in_past_18_months
        current_number_of_H1B_employees
        current_number_of_employees_on_L_visas
        other_visa_statuses_sponsored
        email_address_invoices_should_be_addressed_to
        name_invoices_should_be_addressed_to
        createdAt
        updatedAt
        preferenceForEmployeesToPayPartOfGreenCard {
          value
          reason
        }
        preferenceForEmployeesToPayPartOfH1B {
          value
          reason
        }
        employs50OrMore {
          value
          followPrompt
          followValue
        }
        grossRevenueForMostRecentFiscalYear {
          amount
          currency
        }
        netRevenueForMostRecentFiscalYear {
          amount
          currency
        }
        usesApplicantTrackingSystem {
          reason
          value
        }
        usesHRSSystem {
          reason
          value
        }
        shippingPreference {
          fedexAccount
          preference
          upsAccount
        }
        addressSecondary {
          careOf
          address
          unitNumber
          unitType
          adminArea
          state
          city
          country
          postalCode
          title
          companyAddressType
        }
        addressPrimary {
          careOf
          address
          unitNumber
          unitType
          adminArea
          state
          city
          country
          postalCode
          title
          companyAddressType
        }
      }
      processRequests {
        nextToken
      }
      jobDescriptions {
        nextToken
      }
      files {
        nextToken
      }
      fileRequirements {
        type
        assignedToID
        isAssignedToLegalAssistants
        expirationDateRequired
        issueDateRequired
        isApproved
        isRequested
        durationOfStatus
      }
      cases {
        nextToken
      }
      openCaseCount
      closedCaseCount
      completedCaseCount
      legalTeamIDs
      createdAt
      updatedAt
      companyDataId
    }
  }
`;

export const getNationalHook = /* GraphQL */ `
  query GetNationalHook($id: ID!) {
    getForeignNational(id: $id) {
      id
      email
      avatar {
        original {
          bucket
          identityId
          key
          level
          region
        }
        square {
          bucket
          key
          level
          region
          identityId
        }
        squareThumbnails {
          size
          image {
            bucket
            identityId
            key
            region
            level
          }
        }
      }
      firstname
      lastname
      lowerFull
      immigrationStatus {
        status
        doesNotExpire
        expiration
      }
      citizenshipCountry
      birthCountry
      hasApprovedI140 {
        hasApproved
      }
      aosFilingEligible
      offeredSalary {
        amount
        currency
      }
      isRemote
      companyID
      company {
        id
        name
        displayCompanyName
        slug
        accountManagerID
        assignedAttorneys {
          items {
            attorney {
              id
            }
          }
        }
        assignedParalegals {
          items {
            paralegal {
              id
            }
          }
        }
        assignedEmployers {
          items {
            employer {
              id
            }
          }
        }
        data {
          addressPrimary {
            careOf
            address
            unitNumber
            unitType
            adminArea
            state
            city
            country
            postalCode
            title
          }
          addressSecondary {
            careOf
            address
            unitNumber
            unitType
            adminArea
            state
            city
            country
            postalCode
            title
          }
        }
      }
    }
  }
`;

export const getRelatives = /* GraphQL */ `
  query GetRelatives($id: ID = "") {
    getForeignNational(id: $id) {
      relativesAsSource(limit: 200) {
        items {
          id
          type
          target {
            avatar {
              original {
                bucket
                identityId
                key
                level
                region
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                size
                image {
                  bucket
                  identityId
                  key
                  region
                  level
                }
              }
            }
            type
            email
            firstname
            id
            lastname
            companyID
            slug
            cases {
              items {
                id
                slug
                caseNumber
                startDate
                requestedProcess
                state
                currentPhase
                company {
                  name
                  displayCompanyName
                }
                phaseRequirements {
                  phase
                  milestones
                }
                completedMilestones {
                  completionDate
                  completedByID
                  milestone
                  receiptNumber
                }
              }
            }
            username
            birthCountry
            citizenshipCountry
            aosFilingEligible
            hasApprovedI140 {
              hasApproved
            }
            loginStatus {
              isEnabled
              status
            }
            relativeAdmins
            immigrationStatus {
              expiration
              status
            }
            files {
              items {
                fileType
              }
            }
          }
        }
      }
      relativesAsTarget(limit: 200) {
        items {
          id
          type
          source {
            avatar {
              original {
                bucket
                identityId
                key
                level
                region
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                size
                image {
                  bucket
                  identityId
                  key
                  region
                  level
                }
              }
            }
            type
            email
            firstname
            id
            lastname
            companyID
            slug
            cases {
              items {
                id
                slug
                caseNumber
                requestedProcess
                startDate
                state
                caseManagerID
                caseManager {
                  id
                  firstname
                  lastname
                  slug
                }
                companyID
                company {
                  id
                  name
                  displayCompanyName
                }
                foreignNationalID
                foreignNational {
                  id
                  firstname
                  lastname
                  slug
                  immigrationStatus {
                    expiration
                  }
                }
                phaseRequirements {
                  phase
                  milestones
                }
                completedMilestones {
                  completionDate
                  completedByID
                  milestone
                  receiptNumber
                }
              }
            }
            username
            birthCountry
            citizenshipCountry
            hasApprovedI140 {
              hasApproved
            }
            aosFilingEligible
            loginStatus {
              isEnabled
              status
            }
            relativeAdmins
            immigrationStatus {
              expiration
              status
            }
            files {
              items {
                fileType
              }
            }
          }
        }
      }
    }
  }
`;

export const listCompanyEmployers = /* GraphQL */ `
  query ListCompanyEmployers($id: ID!) {
    getCompany(id: $id) {
      assignedEmployers(limit: 1000) {
        items {
          employer {
            avatar {
              original {
                bucket
                identityId
                key
                level
                region
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                size
                image {
                  bucket
                  identityId
                  key
                  region
                  level
                }
              }
            }
            firstname
            id
            lastname
            type
          }
        }
      }
    }
  }
`;

export const getFirmCompanyCount = /* GraphQL */ `
  query GetFirmCompanyCount($id: ID!) {
    getFirm(id: $id) {
      companyCount
    }
}`;

export const updateFirmCompanyCount = /* GraphQL */ `
  mutation UpdateFirmCompanyCount($id: ID!, $companyCount: Int!) {
    updateFirm(input: { id: $id, companyCount: $companyCount }) {
      companyCount
    }
  }
`;

export const listPreparedFormTemplates = /* GraphQL */ `
  query ListPreparedFormTemplates($id: ID!) {
    getFirm(id: $id) {
      preparedFormTemplates {
        id
        name
        processes
        archived
        updatedAt
        requiresAttorneyApproval
        requiresSponsorApproval
        requiresNationalApproval
        employersCanView
        nationalsCanView
        file {
          bucket
          identityId
          key
          level
          region
        }
      }
    }
  }
`;

export const listFirmContacts = /* GraphQL */ `
  query ListFirmContacts($nextToken: String, $limit: Int) {
    listTeamMembersAlpha(
      archivedLowerFull: { beginsWith: { archived: ACTIVE } }
      limit: $limit
      nextToken: $nextToken
      type: TeamMember
    ) {
      nextToken
      items {
        email
        firstname
        lastname
        lowerFull
        id
        type
        loginStatus {
          isEnabled
          status
        }
        isAdmin
        managerID
        manager {
          id
          firstname
          lastname
          avatar {
            original {
              bucket
              identityId
              key
              level
              region
            }
            square {
              bucket
              key
              level
              region
              identityId
            }
            squareThumbnails {
              size
              image {
                bucket
                identityId
                key
                level
                region
              }
            }
          }
        }
        mostRecentlyInvited
        position
        avatar {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            size
            image {
              bucket
              identityId
              key
              level
              region
            }
          }
        }
        slug
        title
        username
      }
    }
  }
`;

export const listCaseTeamCandidates = /* GraphQL */ `
  query ListCaseTeamCandidates($nextToken: String, $limit: Int) {
    listTeamMembersAlpha(
      archivedLowerFull: { beginsWith: { archived: ACTIVE } }
      limit: $limit
      nextToken: $nextToken
      type: TeamMember
    ) {
      nextToken
      items {
        id
        firstname
        lastname
        lowerFull
        position
        type
      }
    }
  }
`;

export const listAttorneysAndParalegals = /* GraphQL */ `
  query ListAttorneysAndParalegals($nextToken: String) {
    listTeamMembersAlpha(
      nextToken: $nextToken
      archivedLowerFull: { beginsWith: { archived: ACTIVE } }
      filter: {
        or: [{ position: { eq: ATTORNEY } }, { position: { eq: PARALEGAL } }]
      }
      type: TeamMember
    ) {
      nextToken
      items {
        firstname
        lastname
        avatar {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
        }
        id
        type
      }
    }
  }
`;

export const listLegalAssistantIDs = /* GraphQL */ `
  query ListLegalAssistantIDs($nextToken: String) {
    listTeamMembersByPosition(
      nextToken: $nextToken
      archivedLowerFull: { beginsWith: { archived: ACTIVE } }
      position: LEGAL_ASSISTANT
    ) {
      nextToken
      items {
        id
      }
    }
  }
`;

// 'isParent' and 'parentID' are selected for callers that offer companies as
// subsidiary candidates, which must exclude companies that are already a parent
// or already have one. Kept out of the document body so the comment is not sent
// over the wire on every keystroke.
export const searchCompanyNames = /* GraphQL */ `
  query SearchCompanyNames(
    $and: [SearchableCompanyFilterInput]
    $limit: Int = 100
    $nextToken: String
  ) {
    searchCompanies(
      filter: { and: $and }
      nextToken: $nextToken
      limit: $limit
      sort: { field: lowerName, direction: asc }
    ) {
      nextToken
      total
      items {
        id
        name
        displayCompanyName
        isParent
        parentID
        data {
          doingBusinessAs
        }
      }
    }
  }
`;

export const searchCompanyDomains = /* GraphQL */ `
  query SearchCompanyDomains($domain: String, $nextToken: String) {
    searchCompanies(
      filter: { and: [
        { archived: { eq: "ACTIVE"}},
        {domainWhitelist: { match: $domain}}
      ] }
      nextToken: $nextToken
      limit: 100
      sort: { field: displayCompanyName, direction: asc }
    ) {
      nextToken
      total
      items {
        id
        name
        displayCompanyName
      }
    }
  }
`;

export const searchEmployerNames = /* GraphQL */ `
  query SearchEmployerNames(
    $and: [SearchableEmployerFilterInput]
    $limit: Int = 100
    $nextToken: String
  ) {
    searchEmployers(
      filter: { and: $and }
      nextToken: $nextToken
      limit: $limit
      sort: { field: lowerFull, direction: asc }
    ) {
      total
      nextToken
      items {
        id
        email
        firstname
        lastname
        lowerFull
        slug
        avatar {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
          version
        }
      }
    }
  }
`;

export const searchNationalNames = /* GraphQL */ `
  query SearchForeignNationalNames(
    $and: [SearchableForeignNationalFilterInput]
    $limit: Int = 100
    $nextToken: String
  ) {
    searchForeignNationals(
      filter: { and: $and }
      nextToken: $nextToken
      limit: $limit
      sort: { field: lowerFull, direction: asc }
    ) {
      total
      nextToken
      items {
        id
        email
        firstname
        lastname
        lowerFull
        slug
        avatar {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
          version
        }
      }
    }
  }
`;

export const searchTeamMemberNames = /* GraphQL */ `
  query SearchTeamMemberNames(
    $and: [SearchableTeamMemberFilterInput]
    $limit: Int = 100
    $nextToken: String
  ) {
    searchTeamMembers(
      filter: { and: $and }
      nextToken: $nextToken
      limit: $limit
      sort: { field: lowerFull, direction: asc }
    ) {
      nextToken
      total
      items {
        id
        email
        firstname
        lastname
        slug
        avatar {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
          version
        }
      }
    }
  }
`;

export const listForeignNationalsByVisaExpiration = /* GraphQL */ `
  query ListForeignNationalsByVisaExpiration(
    $gte: String = ""
    $and: [SearchableForeignNationalFilterInput]
  ) {
    searchForeignNationals(
      filter: {
        visaExpiration: { gte: $gte }
        not: { visaExpiration: { matchPhrasePrefix: "zzz" } }
        and: $and
      }
      sort: { direction: asc, field: visaExpiration }
      limit: 10
    ) {
      items {
        id
        email
        firstname
        lastname
        slug
        immigrationStatus {
          status
          expiration
        }
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
        }
        company {
          name
          displayCompanyName
        }
      }
      nextToken
    }
  }
`;

export const getEmployerSnapshotCount = /* GraphQL */ `
  query GetEmployerSnapshotCount($id: ID!) {
    getEmployer(id: $id) {
      contributorCases(stateCaseID: { beginsWith: { state: OPEN } }) {
        items {
          caseID
          case {
            id
          }
        }
      }
      sponsoredCases(stateStartDate: { beginsWith: { state: OPEN } }) {
        items {
          id
        }
      }
      processRequestAssignments(
        stateRequestDate: { beginsWith: { state: REQUESTED } }
      ) {
        items {
          id
        }
      }
      tasks(
        filter: {
          state: { eq: TODO },
          caseState: { eq: OPEN }
        },
      ) {
        items {
          id
        }
      }
    }
  }
`;

export const getTeamMemberSnapshotCount = /* GraphQL */ `
  query GetTeamMemberSnapshotCount($id: ID!) {
    getTeamMember(id: $id) {
      attorneyCases(stateStartDate: { beginsWith: { state: OPEN } }) {
        items {
          id
        }
      }
      caseManagerCases(stateStartDate: { beginsWith: { state: OPEN } }) {
        items {
          id
        }
      }
      contributorCases(stateCaseID: { beginsWith: { state: OPEN } }) {
        items {
          case {
            id
          }
        }
      }
      processRequestAssignments(
        stateRequestDate: { beginsWith: { state: REQUESTED } }
      ) {
        items {
          id
        }
      }
      tasks(
        filter: {
          state: { eq: TODO },
          caseState: { eq: OPEN }
        },
      ) {
        items {
          id
        }
      }
    }
  }
`;

// Gotta send ID twice, since match expects string and eq expect ID
export const getForeignNationalSnapshotCount = /* GraphQL */ `
  query GetForeignNationalSnapshotCount($id: ID!) {
    getForeignNational(id: $id) {
      id
      fileRequirements {
        type
        isApproved
        hidden
        durationOfStatus
      }
      files {
        items {
          id
          fileType
        }
      }
      cases (filter: {state: {eq: OPEN}}) {
        items {
          id
          slug
          startDate
          requestedProcess
          requestedProcessSubCategory
          requestedProcessAdditionalProcessing
          globalRequestedProcessSubCategory
          globalProcessCountryCode
          caseNumber
          fileRequirements {
            type
            assignedToID
            isAssignedToLegalAssistants
            expirationDateRequired
            issueDateRequired
            isApproved
            isRequested
            durationOfStatus
          }
        }
      }
      onboardingSections {
        background
        contact
        employment
        family
        immigration
        personal
      }
      relatives
      relativeAdmins
      relativesAsSource {
        items {
          target {
            id
            relativeAdmins
          }
        }
      }
      tasks(
        filter: {
          state: { eq: TODO },
          caseState: { eq: OPEN }
        },
      ) {
        items {
          id
        }
      }
    }
  }
`;

export const createComment = /* GraphQL */ `
  mutation CustomCreateComment(
    $input: CreateCommentInput!
    $condition: ModelCommentConditionInput
  ) {
    createComment(input: $input, condition: $condition) {
      authorID
      authorEmployer {
        id
        lastname
        firstname
        slug
        type
        lowerFirst
        lowerLast
        lowerFull
        archived
        createdAt
        updatedAt
        avatar {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              region
              level
              key
              identityId
              bucket
            }
            size
          }
        }
      }
      authorForeignNational {
        id
        lastname
        firstname
        slug
        type
        lowerFirst
        lowerLast
        lowerFull
        archived
        createdAt
        updatedAt
        avatar {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              region
              level
              key
              identityId
              bucket
            }
            size
          }
        }
      }
      authorTeamMember {
        id
        lastname
        firstname
        slug
        type
        lowerFirst
        lowerLast
        lowerFull
        archived
        createdAt
        updatedAt
        avatar {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              region
              level
              key
              identityId
              bucket
            }
            size
          }
        }
      }
      id
      sourceID
      createdAt
      updatedAt
      note
    }
  }
`;

/* Same as autogen except knowledgeCenterLinks is deepened
   to include file info */
export const customGetFirm = /* GraphQL */ `
  query CustomGetFirm($id: ID!) {
    getFirm(id: $id) {
      id
      name
      slug
      logo {
        version
      }
      domainWhitelist
      address {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      mailingAddress {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      doingBusinessAs
      phone
      fax
      knowledgeCenterLinks {
        id
        text
        file {
          bucket
          key
          level
          region
          identityId
        }
        roles
      }
      shortcuts {
        id
        text
        link
        roles
      }
      holidays
      companyCount
      nationalCount
      preparedFormTemplates {
        id
        name
        processes
        archived
        updatedAt
        requiresAttorneyApproval
        requiresSponsorApproval
        requiresNationalApproval
        employersCanView
        nationalsCanView
      }
      createdAt
      updatedAt
    }
  }
`;

/* Same as autogen except knowledgeCenterLinks is deepened
   to include file info */
export const customUpdateFirm = /* GraphQL */ `
  mutation CustomUpdateFirm(
    $input: UpdateFirmInput!
    $condition: ModelFirmConditionInput
  ) {
    updateFirm(input: $input, condition: $condition) {
      id
      name
      slug
      logo {
        version
      }
      domainWhitelist
      address {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      mailingAddress {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      doingBusinessAs
      phone
      fax
      knowledgeCenterLinks {
        id
        text
        file {
          bucket
          key
          level
          region
          identityId
        }
        roles
      }
      shortcuts {
        id
        text
        link
        roles
      }
      holidays
      companyCount
      nationalCount
      preparedFormTemplates {
        id
        name
        processes
        archived
        updatedAt
        requiresAttorneyApproval
        requiresSponsorApproval
        requiresNationalApproval
        employersCanView
        nationalsCanView
      }
      createdAt
      updatedAt
    }
  }
`;

// Queries the person's own tasks via the byAssigneeByCaseStateByDeadline GSI (a real
// indexed Query), instead of the filtered listTasks scan below — see VOYAG-768.
// NOTE: built by concatenation rather than a template literal. Codegen treats every
// backtick span under src/graphql as a document, and it cannot resolve the
// interpolated root field — one such document fails the entire types run.
export const personTaskCountQuery = (personType: string) => (
  'query GetPersonTaskCount($id: ID!, $nextToken: String) {\n'
  + '  get' + personType + '(id: $id) {\n'
  + '    tasks(\n'
  + '      stateCaseStateDeadline: { beginsWith: { caseState: OPEN, state: TODO } }\n'
  + '      limit: 1000\n'
  + '      nextToken: $nextToken\n'
  + '    ) {\n'
  + '      items {\n'
  + '        id\n'
  + '      }\n'
  + '      nextToken\n'
  + '    }\n'
  + '  }\n'
  + '}\n'
);

export const customListTaskQueryForCount = /* GraphQL */ `
query ListTaskForCount($filter: ModelTaskFilterInput,$limit:Int, $nextToken:String){
  listTasks(filter:$filter,limit:$limit,nextToken:$nextToken){
      items {
        id
        action
        description
        orderIndex
        assignedToID
        isAssignedToLegalAssistants
        prerequisite {
          action
          category
        }
        dependencies {
          items {
            id
            caseState
            state
            deadline
            orderIndex
            duration
            templateID
            startDate
            prerequisiteID
            action
            completeDate
          }
        }
        assignedToEmployer {
          id
          firstname
          lastname
          slug
          avatar {
            original {
              bucket
              key
              level
              region
              identityId
            }
            squareThumbnails {
              image {
                bucket
                key
                level
                region
                identityId
              }
              size
            }
          }
        }
        assignedToTeamMember {
          id
          firstname
          lastname
          position
          slug
          avatar {
            original {
              bucket
              key
              level
              region
              identityId
            }
            squareThumbnails {
              image {
                bucket
                key
                level
                region
                identityId
              }
              size
            }
          }
        }
        assignedToForeignNational {
          id
          firstname
          lastname
          slug
          avatar {
            original {
              bucket
              key
              level
              region
              identityId
            }
            squareThumbnails {
              image {
                bucket
                key
                level
                region
                identityId
              }
              size
            }
          }
        }
        caseID
        caseState
        case {
          id
          slug
          requestedProcess
          attorneyID
          caseManagerID
          companyID
          state
          foreignNational {
            id
            slug
            firstname
            lastname
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
            }
          }
          tasks {
            items {
              action
              category
              state
            }
          }
          contributors {
            items {
              id
              contributorID
              contributorEmployer {
                id
                type
                firstname
                lastname
                slug
                avatar {
                  original {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  squareThumbnails {
                    image {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    size
                  }
                  version
                }
              }
              contributorForeignNational {
                id
                type
                firstname
                lastname
                slug
                avatar {
                  original {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  squareThumbnails {
                    image {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    size
                  }
                  version
                }
              }
              contributorTeamMember {
                id
                type
                firstname
                lastname
                slug
                avatar {
                  original {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  squareThumbnails {
                    image {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    size
                  }
                  version
                }
              }
            }
          }
        }
        category
        companyID
        company {
          id
          name
          displayCompanyName
          slug
        }
        completeDate
        deadline
        deadlineIsManuallySet
        deleteDate
        deleteReason
        duration
        groupsCanRead
        foreignNationalID
        foreignNational {
          id
          slug
          firstname
          lastname
          avatar {
            original {
              bucket
              key
              level
              region
              identityId
            }
            squareThumbnails {
              image {
                bucket
                key
                level
                region
                identityId
              }
              size
            }
          }
        }
        groupsCanRead
        prerequisiteID
        startDate
        state
        templateID
        assigneeLowerName
        attorneyLowerName
        paralegalLowerName
        companyLowerName
        nationalLowerName
        createdAt
        updatedAt
      }
      nextToken
    }
}`;

export const customUpdateJobDescription = /* GraphQL */ `
  mutation CustomUpdateJobDescription(
    $input: UpdateJobDescriptionInput!
    $condition: ModelJobDescriptionConditionInput
  ) {
    updateJobDescription(input: $input, condition: $condition) {
      companyID
      id
      archived
      type
      foreignNationalID
      title
      officialTitle
      degrees
      requiresBachelors
      requiresMasters
      requiresPhd
      dotNumber
      caseSocCode
      duties
      jobURL
      majors
      specialRequirements
      yearsGeneralExperience
      objectiveSkillSetsRequired
      offeredSalary
      certificationLicenses
      travelIsRequired
      travelPercentage
      travelDomestic
      travelInternational
      supervisesOthers
      supervisesOthersCount
      supervisesTitles
      supervisorTitle
      canWorkFromHome
      uploadedDescriptionID
      uploadedDescription {
        id
        expirationDate
        fileName
        fileType
        contentType
        processRequestID
        companyID
        ownerNationalID
        sourceID
        uploadDate
        uploaderID
        uploaderEmployerID
        uploaderForeignNationalID
        uploaderTeamMemberID
        createdAt
        updatedAt

        original{
          bucket
          region
          key
          level
          identityId
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const CustomlistForeignNationals = /* GraphQL */ `
  query CustomlistForeignNationals(
    $filter: ModelForeignNationalFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listForeignNationals(
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        type
        id
        email
        emailPersonal
        emailWork
        username
        firstname
        middlename
        lastname
        immigrationStatus {
          status
          expiration
        }
        immigrationVisaStatus
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const getlistForeignNationals = /* GraphQL */ `
  query GetlistForeignNationals(
    $filter: ModelForeignNationalFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listForeignNationals(
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        type
        id
        firstname
        middlename
        lastname
        visaExpiration
        visaExpDate
      }
      nextToken
    }
  }
`;

export const getCompanyForeignNationals = /* GraphQL */ `
  query GetCompanyForeignNationals($id: ID = "") {
    getCompany(id: $id) {
      foreignNationals(limit: 300) {
      items {
        companyName
        id
      }
      nextToken
    }
    }
  }
`;

export const listCompanies = /* GraphQL */`
  query listCompanies($nextToken: String $limit: Int){
    listCompanies(
      limit: $limit
      nextToken: $nextToken
    ){
      items {
        name
        displayCompanyName
        id
        data {
          doingBusinessAs
        }
        assignedEmployerAdmins {
          items {
            employer {
              id
              firstname
              lastname
              middlename
            }
          }
        }
        assignedEmployers {
          items {
            employer {
              id
              firstname
              lastname
              middlename
            }
          }
        }
      }
      nextToken
    }
  } 
`;

export const getEmployerCompanies = /* GraphQL */`
  query GetEmployerCompanies($id: ID!, $nextToken: String){
    getEmployer(id: $id) {
      companies(nextToken: $nextToken) {
        items {
          company {
            id
            name
            displayCompanyName
            archived
            data {
              doingBusinessAs
            }
            accountManager {
              firstname
              lastname
              middlename
              id
            }
            assignedAttorneys {
              items {
                attorney {
                  id
                  lastname
                  firstname
                  middlename
                }
              }
            }
            assignedParalegals {
              items {
                paralegal {
                  id
                  firstname
                  lastname
                  middlename
                }
              }
            }
            assignedEmployers {
              items {
                employer {
                  middlename
                  lastname
                  firstname
                  id
                  slug
                  avatar {
                    original {
                      bucket
                      identityId
                      key
                      level
                      region
                    }
                    square {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    squareThumbnails {
                      image {
                        bucket
                        identityId
                        key
                        level
                        region
                      }
                      size
                    }
                    version
                  }
                }
              }
            }
          }
        }
        nextToken
      }
    }
  }
`;

export const listAllTeamMembers = /* GraphQL */`
  query ListAllTeamMembers($limit: Int, $nextToken: String) {
    listTeamMembers(limit: $limit, nextToken: $nextToken) {
      items {
        id
        firstname
        lastname
        middlename
      }
      nextToken
    }
  }
`;

export const getSelectedFields = /* GraphQL */ `
  query GetSelectedFields($id: ID!) {
    getReports(id: $id) {
      id
      templateID
      customTemplate {
        accessibleCompanyIds
        accessibleIds
        category
        companyIds
        createdAt
        editAccess
        editableIds
        employerID
        id
        includeCases
        includeFN
        individualFNCase
        isDeleted
        name
        numberOfColumns
        numberOfFilters
        selectedFields
        teamMemberId
        type
        updatedAt
        viewAccess
      }
    }
  }
`;

export const customSearchReportEntries = /* GraphQL */ `
  query CustomSearchReportEntries(
    $filter: SearchableReportEntriesFilterInput
    $sort: [SearchableReportEntriesSortInput]
    $limit: Int
    $nextToken: String
  ) {
    searchReportEntries(
      filter: $filter
      sort: $sort
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        reportID
        companyID
        foreignNationalID
        fullname
        firstname
        lastname
        middlename
        archived
        email
        personalEmail
        birthDate
        numberOfDependents
        numberOfFamilyContacts
        displayCompanyName
        companyLegalName
        companyFullName
        companyDbaName
        immigrationCurrentStatus
        immigrationStatusExpiration
        birthCountryCode
        citizenshipCountry
        chargeabilityCountry
        passportExpirationDate
        nivMaxout
        visaExpDate
        hasApprovedI140
        aosFilingEligible
        requiresMeetingReminder
        annualMeetingDate
        isParent
        isPartnerCompany
        petitionerI140
        employmentBasedCategory
        priorityDate
        fnJobTitle
        socCode
        caseSocCode
        fnSalary
        startDate
        fnWorkLocation
        addressPrimary
        addressSecondary
        workLocation
        usResidentialAddress
        lastProcessType
        lowerProcessType
        currentProcessType
        lastPhaseCompleted
        currentPhase
        capType
        eadExpDate
        I797ApprovalExpDate
        I94ExpDate
        I94DS
        greenCardExpDate
        prevailingWageExpDate
        citizenshipEligibilityDate
        rfeDueDate
        dolAuditDueDate
        pwdExpDate
        firstEntryUS
        taxTrackingF1J1
        agreementExpDate
        agreementEffectiveDate
        mswsowExpDate
        permDueDate
        lotterySelectionDueDate
        advancedParoleExpDate
        reEntryPermitExpDate
        certifiedLcaExpDate
        title
        notes
        DS2019ExpDate
        isRelative
        relativeIds
        nextPhase
        requestedProcessSubCategory
        fnNameSlug
        companyNameSlug
        employerNameSlug
        caseSlug
        inviteStatus
        passportGender
        passportNumber
        phoneNumber
        personalPhone
        faxNumber
        companyAttorneys
        companyAttorneyIds
        companyParalegals
        companyParalegalIds
        companyTotalCases
        companyTotalOpenCases
        companyTotalClosedCases
        companyTotalCompletedCases
        companyNationalCount
        companyActiveNationalCount
        fullTimeEmployeesInUS
        partTimeEmployeesInUS
        preferredMailingAddress
        companyWhitelistedDomains
        companyContactLabel
        typeOfBusiness
        fein
        naicsCodeOfCompany
        yearCompanyWasFoundedInUS
        grossRevenueForMostRecentFiscalYear
        netRevenueForMostRecentFiscalYear
        onboardingSections
        lastEntryUS
        employeeId
        caseNumber
        requestedProcess
        attorneyID
        caseAttorney
        sponsoringEmployerID
        sponsoringNationalID
        caseSponsor
        caseManagerID
        caseManager
        contributorIds
        caseContributors
        caseStartDate
        caseOpenDate
        caseSalary
        state
        sponsoringCompanyFullName
        keyFilingDates
        selection
        registrationCompleted
        lcaFiled
        caseNature
        capGap
        visaAdditionalProcessing
        stemExtEligible
        educationEvaluationNeeded
        wageIssues
        receiptNumber
        currentCaseStatusText
        modifiedDate
        incompleteUscisReceiptNumber
        caseStatusHistory        
        emCompanyNames
        emCompanyNameSlugs
        createdAt
        updatedAt
      }
      nextToken
      total
    }
  }
`;

export const listAllReports = /* GraphQL */ `
  query ListAllReports(
    $filter: ModelReportFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listReports(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        companyIds
        id
        createdAt
        name
        status
        templateID
        updatedAt
        accessibleIds
        customTemplate {
          selectedFields
        }
      }
      nextToken
    }
  }
`;

export const listMyTemplates = /* GraphQL */ `
  query ListMyTemplates(
    $filter: ModelCustomTemplateFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listCustomTemplate(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        employer {
          firstname
          lastname
        }
        teamMember {
          lastname
          firstname
        }
        createdAt
      }
      nextToken
    }
  }
`;

export const searchCompanyCases = /* GraphQL */ `
  query SearchCompanyCases(
    $filter: SearchableCompanyFilterInput
    $sort: [SearchableCompanySortInput]
    $limit: Int
    $nextToken: String
    $from: Int
    $aggregates: [SearchableCompanyAggregationInput]
  ) {
    searchCompanies(
      filter: $filter
      sort: $sort
      limit: $limit
      nextToken: $nextToken
      from: $from
      aggregates: $aggregates
    )  {
    items {
      name
      displayCompanyName
      legalTeamIDs
      cases(limit: $limit) {
        items {
          sponsoringEmployer {
            id
            firstname
            middlename
            lastname
          }
          contributors {
            items {
              contributorTeamMember {
                id
                firstname
                middlename
                lastname
              }
              contributorEmployer {
                id
                firstname
                middlename
                lastname
              }
            }
          }
          caseManager {
            id
            firstname
            middlename
            lastname
          }
          attorney {
            id
            firstname
            middlename
            lastname
          }
        }
      }
    }
  }
  }
`;

export const customListEmployers = /* GraphQL */ `
  query CustomListEmployers(
    $filter: ModelEmployerFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listEmployers(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        firstname
        middlename
        lastname
        slug
        companies {
          items {
            company {
              displayCompanyName
              name
              id
            }
          }
        }
        avatar {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
          version
        }
      }
      nextToken
    }
  }
`;

export const customListTeamMembers = /* GraphQL */ `
  query CustomListTeamMembers(
    $filter: ModelTeamMemberFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listTeamMembers(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        firstname
        middlename
        lastname
        slug
        avatar {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
          version
        }
      }
      nextToken
    }
  }
`;

export const listCompanyAllEmployers = /* GraphQL */ `
  query ListCompanyAllEmployers(
    $filter: ModelCompanyFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listCompanies(filter: $filter, limit: $limit, nextToken: $nextToken) {
    items {
      assignedEmployers {
        items {
          employer {
            firstname
            middlename
            lastname
            id
          }
        }
      }
      legalTeamIDs
    }
    nextToken
  }
  }
`;

export const customListSchedulers = /* GraphQL */ `
  query CustomListSchedulers(
    $filter: ModelSchedulerFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listSchedulers(filter: $filter, limit: $limit, nextToken: $nextToken) {
    items {
      id
      name
      eventBridgeschedulerName
      description
      startDate
      endDate     
      timeZone
      startTime
      frequencyNumber
      frequencyUnit
      entityID
      taskDefinition {
        id
        recipientIDs
        individualIDs
        companyIDs
      }
    }
  }
  }
`;

export const customSearchTemplates = /* GraphQL */ `
  query CustomSearchTemplates(
    $filter: SearchableCustomTemplateFilterInput
    $sort: [SearchableCustomTemplateSortInput]
    $limit: Int
    $nextToken: String
    $from: Int
    $aggregates: [SearchableCustomTemplateAggregationInput]
  ) {
    searchCustomTemplates(
      filter: $filter
      sort: $sort
      limit: $limit
      nextToken: $nextToken
      from: $from
      aggregates: $aggregates
    ) {
      items {
        id
        teamMemberId
        employerID
        accessibleIds
        editableIds
        name
        selectedFields
        includeFN
        individualFNCase
        includeCases
        numberOfFilters
        numberOfColumns
        type
        category
        isDeleted
        viewAccess
        editAccess
        accessibleCompanyIds
        companyIds
        templateReports {
          items {
            id
            status
            createdAt
          }
        }
        createdAt
        updatedAt
      }
      nextToken
      total
      aggregateItems {
        name
        result {
          ... on SearchableAggregateScalarResult {
            value
          }
          ... on SearchableAggregateBucketResult {
            buckets {
              key
              doc_count
            }
          }
        }
      }
    }
  }
`;

export const customSearchCompanies = /* GraphQL */ `
  query CustomSearchCompanies(
    $filter: SearchableCompanyFilterInput
    $sort: [SearchableCompanySortInput]
    $limit: Int
    $nextToken: String
    $from: Int
    $aggregates: [SearchableCompanyAggregationInput]
  ) {
    searchCompanies(
      filter: $filter
      sort: $sort
      limit: $limit
      nextToken: $nextToken
      from: $from
      aggregates: $aggregates
    ) {
      items {
        id
        name
        lowerName
        lowerNameKey
        displayCompanyName
        slug
        archived
        companyNumber
        nationalCount
        accountManagerID
        legalTeamIDs
        companyDataId
        data {
          doingBusinessAs
        }
        logo {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            identityId
            key
            level
            region
          }
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
          }
          thumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
          version
        }
      }
      nextToken
      total
      aggregateItems {
        name
        result {
          ... on SearchableAggregateScalarResult {
            value
          }
          ... on SearchableAggregateBucketResult {
            buckets {
              key
              doc_count
            }
          }
        }
      }
    }
  }
`;

export const searchCustomEmployers = /* GraphQL */ `
  query SearchCustomEmployers(
    $filter: SearchableEmployerFilterInput
    $sort: [SearchableEmployerSortInput]
    $limit: Int
    $nextToken: String
    $from: Int
    $aggregates: [SearchableEmployerAggregationInput]
  ) {
    searchEmployers(
      filter: $filter
      sort: $sort
      limit: $limit
      nextToken: $nextToken
      from: $from
      aggregates: $aggregates
    )  {
      total
      nextToken
      items {
        id
        firstname
        lastname
        middlename
        slug
        companies {
          items {
            company {
              displayCompanyName
              name
              id
            }
          }
        }
        avatar {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
          version
        }
      }
    }
  }
`;

export const customSearchTeamMembers = /* GraphQL */ `
  query CustomSearchTeamMembers(
    $filter: SearchableTeamMemberFilterInput
    $sort: [SearchableTeamMemberSortInput]
    $limit: Int
    $nextToken: String
    $from: Int
    $aggregates: [SearchableTeamMemberAggregationInput]
  ) {
    searchTeamMembers(
      filter: $filter
      sort: $sort
      limit: $limit
      nextToken: $nextToken
      from: $from
      aggregates: $aggregates
    ) {
      total
      nextToken
      items {
        id
        firstname
        lastname
        middlename
        slug
        position
        avatar {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
          version
        }
      }
    }
  }
`;

export const customGetForeignNational = /* GraphQL */ `
  query CustomGetForeignNational($id: ID!) {
    getForeignNational(id: $id) {
      type
      id
      createdBy
      slug
      userAgreementAcknowledged
      userAgreementAcknowledgementDate
      userAgreementAcknowledgementText
      hasLoggedIn
      onboardingSections {
        background
        contact
        employment
        family
        immigration
        personal
      }
      completedOnboardingQuestionsBy
      email
      emailPersonal
      emailWork
      usMailingAddress {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      usResidentialAddress {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      lastAddressOutsideUS {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      username
      avatar {
        version
      }
      firstname
      middlename
      lastname
      nickname
      employeeId
      additionalFirstnames
      additionalMiddlenames
      additionalLastnames
      lowerFirst
      lowerLast
      lowerFull
      maritalStatus
      spouse
      dependents
      phone
      phoneWork
      companyID
      company {
        id
        name
        lowerName
        lowerNameKey
        displayCompanyName
        slug
        type
        domainWhitelist
        hasCompletedOnboardingQuestions
        completedOnboardingQuestionsBy
        isPartnerCompany
        isParent
        parentID
        archived
        companyNumber
        nationalCount
        accountManagerID
        openCaseCount
        closedCaseCount
        completedCaseCount
        legalTeamIDs
        notes
        createdAt
        updatedAt
        companyDataId
      }
      companyNationalNumber
      mostRecentlyInvited
      archived
      workLocation {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      relativesAsSource {
        nextToken
      }
      relativesAsTarget {
        nextToken
      }
      relatives
      relativeAdmins
      immigrationStatus {
        status
        doesNotExpire
        expiration
      }
      immigrationVisaStatus
      visaExpiration
      visaExpDate
      nonImmigrantVisaNumber
      visaIssueDate
      visaConsulate
      immigrantVisaApplicant
      immigrantPetitionEverFiled
      j1j2 {
        hasHad
        start
        end
      }
      hasHadI485Filed {
        value
        followPrompt
        followValue
      }
      hasPendingETA9089
      aosFilingEligible
      hasApprovedI140 {
        hasApproved
        classification
        employmentBasedCategory
        priorityDate
        petitionerI140
      }
      hasValidOrExpiredEAD
      hasPendingUSCISApplication
      hasHadVisaPetitionRevokedOrDenied
      hasBeenArrestedSinceLastUSEntry
      criminalHistory {
        tortureGenocide
        killing
        severeInjuring
        nonconsensualSex
        religiousBelief
      }
      hasTransportedWeapons
      hasParticipatedInArmedGroup
      hasDetailedPerson
      hasUsedWeapons
      hasWeaponsTraining
      hasCriminalOffence
      hasViolatedVisaTerms
      isInRemovalProceedings {
        value
        reason
      }
      hasBeenEmployedSinceLastAdmitted {
        value
        reason
      }
      citizenshipCountry
      birthCountry
      birthState
      birthCity
      birthDate
      jobDescriptionID
      jobDescription {
        companyID
        id
        archived
        type
        foreignNationalID
        title
        officialTitle
        degrees
        requiresBachelors
        requiresMasters
        requiresPhd
        dotNumber
        caseSocCode
        duties
        jobURL
        majors
        specialRequirements
        yearsGeneralExperience
        objectiveSkillSetsRequired
        offeredSalary
        certificationLicenses
        travelIsRequired
        travelPercentage
        travelDomestic
        travelInternational
        supervisesOthers
        supervisesOthersCount
        supervisesTitles
        supervisorTitle
        canWorkFromHome
        uploadedDescriptionID
        createdAt
        updatedAt
      }
      nivMaxout
      offeredSalary {
        currency
        amount
        period
      }
      isRemote
      loginStatus {
        isEnabled
        status
      }
      hasNonUSPassport
      passportGender
      passportIssueDate
      passportExpirationDate
      passportNumber
      passportCountry
      usSSN
      hasSSN
      aNumber
      lastEntryUS
      i94ArrivalDepartureRecordNumber
      sevisNumber
      eadNumber
      cases {
        nextToken
      }
      sponsoredCases {
        nextToken
      }
      processRequests {
        nextToken
      }
      tasks {
        nextToken
      }
      files {
        nextToken
      }
      fileRequirements {
        type
        assignedToID
        isAssignedToLegalAssistants
        expirationDateRequired
        issueDateRequired
        dueDateRequired
        isApproved
        isRequested
        hidden
        durationOfStatus
      }
      fileRequirementsManipulated
      test
      notes
      companyName
      immigrationCurrentStatus
      immigrationStatusExpiration
      employmentBasedCategory
      priorityDate
      eadExpDate
      I797ExpDate
      visaExpDate
      chargeabilityCountry
      birthCountryCode
      socCode
      firstEntryUS
      taxTrackingF1J1
      startDate
      legalTeamIDs
      createdAt
      updatedAt
    }
  }
`;

export const customSearchSchedulers = /* GraphQL */ `
  query CustomSearchSchedulers(
    $filter: SearchableSchedulerFilterInput
    $sort: [SearchableSchedulerSortInput]
    $limit: Int
    $nextToken: String
    $from: Int
    $aggregates: [SearchableSchedulerAggregationInput]
  ) {
    searchSchedulers(
      filter: $filter
      sort: $sort
      limit: $limit
      nextToken: $nextToken
      from: $from
      aggregates: $aggregates
    ) {
      items {
        id
        name
        eventBridgeschedulerName
        description
        startDate
        endDate
        timeZone
        startTime
        frequencyNumber
        frequencyUnit
        entityID
        taskDefinition {
          id
          recipientIDs
          individualIDs
          companyIDs
        }
      }
      nextToken
      total
    }
  }
`;

export const getReportStatus = /* GraphQL */ `
  query GetReportStatus($id: ID!) {
    getReports(id: $id) {
      id
      status
    }
  }
`;
